import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import { PhotoCamera, Add as AddIcon } from '@mui/icons-material';
import {
  calculateImagePinchZoom,
  calculateImageDragPosition,
  calculateImageWheelZoom,
  clampImageZoom,
  imageCropBackgroundSize,
  renderCroppedImage,
} from '../utils/imageCrop';

/* ===== CurrencyBRL (máscara BRL, limpa zeros ao focar) ===== */
const brFmt = (n) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number.isFinite(n) ? n : 0);

export function CurrencyBRL({ label, value = 0, onChange, helperText, disabled = false, ...props }) {
  const [display, setDisplay] = useState(value ? brFmt(value) : '0,00');

  useEffect(() => {
    // sincroniza quando value externo muda (se o campo não estiver sendo editado)
    setDisplay(value ? brFmt(value) : '0,00');
  }, [value]);

  const toNumber = (digits) => {
    if (!digits) return 0;
    const cents = parseInt(digits, 10);
    return Number.isNaN(cents) ? 0 : cents / 100;
  };

  const handleFocus = () => {
    if (!value) setDisplay('');
  };

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (!digits) {
      setDisplay('');
      onChange?.(0);
      return;
    }
    const num = toNumber(digits);
    setDisplay(brFmt(num));
    onChange?.(num);
  };

  const handleBlur = () => {
    if (display === '' || display == null) {
      setDisplay('0,00');
      onChange?.(0);
    }
  };

  return (
    <TextField
      label={label}
      value={display}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      fullWidth
      margin="normal"
      inputMode="numeric"
      placeholder="0,00"
      disabled={disabled}
      InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography> }}
      helperText={helperText}
      {...props}
    />
  );
}

/* ===== Upload com ajuste de enquadramento ===== */
const IMAGE_MAX_SIZE = 8 * 1024 * 1024;
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const IMAGE_ZOOM_MIN = 1;
const IMAGE_ZOOM_MAX = 4;

const EMPTY_IMAGE_EDITOR = {
  source: '',
  naturalWidth: 0,
  naturalHeight: 0,
  zoom: 1,
  positionX: 50,
  positionY: 50,
};

export const ImageUpload = ({
  image,
  onImageChange,
  onImageAdjusted,
  accept = 'image/jpeg,image/png,image/webp',
  disabled = false,
  label = 'Foto do item',
  recommendedSize = '800 × 800 px',
  outputWidth = 800,
  outputHeight = 800,
  outputMimeType = 'image/webp',
  size,
  previewWidth = size || 180,
  previewHeight = size || 180,
}) => {
  const cropImageRef = useRef(null);
  const cropFrameRef = useRef(null);
  const cropDragRef = useRef(null);
  const cropPointersRef = useRef(new Map());
  const cropPinchRef = useRef(null);
  const [editor, setEditor] = useState(EMPTY_IMAGE_EDITOR);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const [dragging, setDragging] = useState(false);

  const closeEditor = () => {
    if (applying) return;
    cropDragRef.current = null;
    cropPointersRef.current.clear();
    cropPinchRef.current = null;
    setDragging(false);
    setEditor(EMPTY_IMAGE_EDITOR);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!onImageAdjusted) {
      onImageChange?.(event);
      return;
    }

    event.target.value = '';
    if (!IMAGE_TYPES.has(file.type)) {
      setError('Envie uma imagem JPG, PNG ou WebP.');
      return;
    }
    if (file.size > IMAGE_MAX_SIZE) {
      setError('A imagem deve ter no máximo 8 MB.');
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      setEditor({
        ...EMPTY_IMAGE_EDITOR,
        source: String(reader.result || ''),
      });
    };
    reader.onerror = () => setError('Não foi possível abrir essa imagem.');
    reader.readAsDataURL(file);
  };

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setEditor((current) => ({ ...current, naturalWidth, naturalHeight }));
  };

  const handleApply = () => {
    setApplying(true);
    try {
      const adjustedImage = renderCroppedImage({
        image: cropImageRef.current,
        outputWidth,
        outputHeight,
        zoom: editor.zoom,
        positionX: editor.positionX,
        positionY: editor.positionY,
        mimeType: outputMimeType,
      });
      onImageAdjusted(adjustedImage);
      setEditor(EMPTY_IMAGE_EDITOR);
      setError('');
    } catch (cropError) {
      setError(cropError?.message || 'Não foi possível ajustar essa imagem.');
    } finally {
      setApplying(false);
    }
  };

  const previewAspect = outputWidth / outputHeight;
  const editorBackgroundSize = imageCropBackgroundSize({
    naturalWidth: editor.naturalWidth,
    naturalHeight: editor.naturalHeight,
    outputWidth,
    outputHeight,
    zoom: editor.zoom,
  });
  const [backgroundWidthPercent = 100, backgroundHeightPercent = 100] =
    editorBackgroundSize === 'cover'
      ? [100, 100]
      : editorBackgroundSize.split(' ').map((value) => Number.parseFloat(value));

  const handleCropPointerDown = (event) => {
    if (!editor.naturalWidth || applying) return;
    event.preventDefault();
    cropPointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    event.currentTarget.setPointerCapture?.(event.pointerId);

    if (cropPointersRef.current.size >= 2) {
      const [first, second] = Array.from(cropPointersRef.current.values());
      cropPinchRef.current = {
        startDistance: Math.hypot(second.x - first.x, second.y - first.y),
        startZoom: editor.zoom,
      };
      cropDragRef.current = null;
      setDragging(true);
      return;
    }

    cropDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      positionX: editor.positionX,
      positionY: editor.positionY,
    };
    setDragging(true);
  };

  const handleCropPointerMove = (event) => {
    if (cropPointersRef.current.has(event.pointerId)) {
      cropPointersRef.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
    }

    if (cropPointersRef.current.size >= 2 && cropPinchRef.current) {
      const [first, second] = Array.from(cropPointersRef.current.values());
      const currentDistance = Math.hypot(second.x - first.x, second.y - first.y);
      setEditor((current) => ({
        ...current,
        zoom: calculateImagePinchZoom({
          zoom: cropPinchRef.current.startZoom,
          startDistance: cropPinchRef.current.startDistance,
          currentDistance,
          min: IMAGE_ZOOM_MIN,
          max: IMAGE_ZOOM_MAX,
        }),
      }));
      return;
    }

    const drag = cropDragRef.current;
    const frame = cropFrameRef.current;
    if (!drag || !frame || drag.pointerId !== event.pointerId) return;

    const rect = frame.getBoundingClientRect();
    const overflowX = rect.width * Math.max(0, backgroundWidthPercent / 100 - 1);
    const overflowY = rect.height * Math.max(0, backgroundHeightPercent / 100 - 1);
    setEditor((current) => ({
      ...current,
      positionX: calculateImageDragPosition({
        startPosition: drag.positionX,
        delta: event.clientX - drag.startX,
        overflow: overflowX,
      }),
      positionY: calculateImageDragPosition({
        startPosition: drag.positionY,
        delta: event.clientY - drag.startY,
        overflow: overflowY,
      }),
    }));
  };

  const handleCropPointerUp = (event) => {
    cropPointersRef.current.delete(event.pointerId);
    cropDragRef.current = null;
    cropPinchRef.current = null;
    setDragging(cropPointersRef.current.size > 0);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const updateZoom = (value) => {
    const nextZoom = clampImageZoom(value, IMAGE_ZOOM_MIN, IMAGE_ZOOM_MAX);
    setEditor((current) => ({ ...current, zoom: nextZoom }));
  };

  const handleCropWheel = (event) => {
    if (!editor.naturalWidth || applying) return;
    event.preventDefault();
    updateZoom(calculateImageWheelZoom({
      zoom: editor.zoom,
      deltaY: event.deltaY,
      min: IMAGE_ZOOM_MIN,
      max: IMAGE_ZOOM_MAX,
    }));
  };

  return (
    <Stack spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
      <Paper
        variant="outlined"
        sx={{
          p: image ? 0 : 2,
          border: '2px dashed #ddd',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: previewHeight,
          width: previewWidth,
          maxWidth: '100%',
          overflow: 'hidden',
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: '#fafafa',
          opacity: disabled ? 0.65 : 1,
          '&:hover': { borderColor: disabled ? '#ddd' : 'primary.main' },
        }}
        component="label"
        aria-label={`Escolher e ajustar ${label.toLowerCase()}`}
      >
        <input type="file" hidden onChange={handleFileChange} accept={accept} disabled={disabled} />
        {image ? (
          <img
            src={image}
            alt={`Prévia de ${label.toLowerCase()}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <>
            <PhotoCamera sx={{ fontSize: 40, color: 'grey.500' }} />
            <Typography variant="body2" color="text.secondary" mt={1} textAlign="center">
              Escolher e ajustar
            </Typography>
          </>
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: Math.max(previewWidth, 220) }}>
        {label}: recomendado {recommendedSize}. JPG, PNG ou WebP, até 8 MB.
        {image ? ' Clique na imagem para trocar e ajustar o enquadramento.' : ''}
      </Typography>
      {error && !editor.source && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}

      <Dialog open={Boolean(editor.source)} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>Ajustar {label.toLowerCase()}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.25}>
            <Alert severity="info">
              Resultado final: {recommendedSize}. Arraste a imagem e use o zoom para escolher o enquadramento.
            </Alert>

            <Box
              ref={cropFrameRef}
              aria-label="Pré-visualização do enquadramento"
              onPointerDown={handleCropPointerDown}
              onPointerMove={handleCropPointerMove}
              onPointerUp={handleCropPointerUp}
              onPointerCancel={handleCropPointerUp}
              onWheel={handleCropWheel}
              sx={{
                width: '100%',
                maxWidth: 420,
                aspectRatio: String(previewAspect),
                mx: 'auto',
                borderRadius: 2,
                border: '3px dashed',
                borderColor: 'primary.main',
                backgroundColor: 'grey.200',
                backgroundImage: editor.source ? `url("${editor.source}")` : 'none',
                backgroundRepeat: 'no-repeat',
                backgroundSize: editorBackgroundSize,
                backgroundPosition: `${editor.positionX}% ${editor.positionY}%`,
                cursor: dragging ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
                boxShadow: '0 0 0 5px rgba(25, 118, 210, 0.08)',
                position: 'relative',
              }}
            >
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  borderRadius: 999,
                  bgcolor: 'rgba(15, 23, 42, 0.72)',
                  color: '#fff',
                  px: 1,
                  py: 0.35,
                  fontSize: 12,
                  fontWeight: 700,
                  pointerEvents: 'none',
                }}
              >
                {Math.round(editor.zoom * 100)}%
              </Box>
            </Box>

            {editor.source ? (
              <img
                ref={cropImageRef}
                src={editor.source}
                alt=""
                hidden
                data-testid="image-crop-source"
                onLoad={handleImageLoad}
                onError={() => {
                  setError('Não foi possível abrir essa imagem.');
                  setEditor(EMPTY_IMAGE_EDITOR);
                }}
              />
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Arraste para posicionar. Use o scroll do mouse para aplicar zoom ou, no celular, afaste e aproxime dois dedos.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setEditor((current) => ({
                  ...current,
                  zoom: IMAGE_ZOOM_MIN,
                  positionX: 50,
                  positionY: 50,
                }))}
                disabled={applying}
                sx={{ flexShrink: 0 }}
              >
                Redefinir enquadramento
              </Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={applying}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={applying || !editor.naturalWidth}
          >
            {applying ? 'Preparando...' : 'Usar imagem ajustada'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

/* ===== Ícones de tags ===== */
const getTagIcon = (tagName = '') => {
  const lowerCaseName = (tagName || '').toLowerCase();
  const svgProps = {
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.5',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  if (lowerCaseName.includes('levemente apimentado'))
    return (
      <svg {...svgProps}>
        <path d="M12.5 13.5c0-2 2-3 2-3s-2-1-2-3c0-2-2.5-4-2.5-4s-2.5 2-2.5 4c0 2-2 3-2 3s2 1 2 3c0 2 0 4.5 0 4.5s2.5 2 2.5 2 2.5-2 2.5-2S12.5 15.5 12.5 13.5z" />
        <path d="M9 14.5c0 2 1 3.5 1 3.5" />
      </svg>
    );
  if (lowerCaseName.includes('muito apimentado'))
    return (
      <svg {...svgProps}>
        <g>
          <path d="M12.5 13.5c0-2 2-3 2-3s-2-1-2-3c0-2-2.5-4-2.5-4s-2.5 2-2.5 4c0 2-2 3-2 3s2 1 2 3c0 2 0 4.5 0 4.5s2.5 2 2.5 2 2.5-2 2.5-2S12.5 15.5 12.5 13.5z" />
          <path d="M9 14.5c0 2 1 3.5 1 3.5" />
        </g>
        <g transform="translate(4, 0)">
          <path
            d="M12.5 13.5c0-2 2-3 2-3s-2-1-2-3c0-2-2.5-4-2.5-4s-2.5 2-2.5 4c0 2-2 3-2 3s2 1 2 3c0 2 0 4.5 0 4.5s2.5 2 2.5 2 2.5-2 2.5-2S12.5 15.5 12.5 13.5z"
            opacity="0.6"
          />
          <path d="M9 14.5c0 2 1 3.5 1 3.5" opacity="0.6" />
        </g>
        <g transform="translate(-4, 0)">
          <path
            d="M12.5 13.5c0-2 2-3 2-3s-2-1-2-3c0-2-2.5-4-2.5-4s-2.5 2-2.5 4c0 2-2 3-2 3s2 1 2 3c0 2 0 4.5 0 4.5s2.5 2 2.5 2 2.5-2 2.5-2S12.5 15.5 12.5 13.5z"
            opacity="0.6"
          />
          <path d="M9 14.5c0 2 1 3.5 1 3.5" opacity="0.6" />
        </g>
      </svg>
    );
  if (lowerCaseName.includes('apimentado'))
    return (
      <svg {...svgProps}>
        <g>
          <path d="M12.5 13.5c0-2 2-3 2-3s-2-1-2-3c0-2-2.5-4-2.5-4s-2.5 2-2.5 4c0 2-2 3-2 3s2 1 2 3c0 2 0 4.5 0 4.5s2.5 2 2.5 2 2.5-2 2.5-2S12.5 15.5 12.5 13.5z" />
          <path d="M9 14.5c0 2 1 3.5 1 3.5" />
        </g>
        <g transform="translate(4, 0)">
          <path
            d="M12.5 13.5c0-2 2-3 2-3s-2-1-2-3c0-2-2.5-4-2.5-4s-2.5 2-2.5 4c0 2-2 3-2 3s2 1 2 3c0 2 0 4.5 0 4.5s2.5 2 2.5 2 2.5-2 2.5-2S12.5 15.5 12.5 13.5z"
            opacity="0.6"
          />
          <path d="M9 14.5c0 2 1 3.5 1 3.5" opacity="0.6" />
        </g>
      </svg>
    );
  if (lowerCaseName.includes('sem açúcar'))
    return (
      <svg {...svgProps}>
        <path d="M18 8.5V7a1 1 0 00-1-1h-1a1 1 0 00-1 1v1.5M15 11h-1a1 1 0 00-1 1v1.5h3V12a1 1 0 00-1-1zM12 13.5V12a1 1 0 00-1-1h-1a1 1 0 00-1 1v1.5M9 11H8a1 1 0 00-1 1v1.5h3V12a1 1 0 00-1-1zM6 8.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v1.5h3z" />
        <path d="M3 3l18 18" />
      </svg>
    );
  if (lowerCaseName.includes('contém açúcar'))
    return (
      <svg {...svgProps}>
        <path d="M18 8.5V7a1 1 0 00-1-1h-1a1 1 0 00-1 1v1.5M15 11h-1a1 1 0 00-1 1v1.5h3V12a1 1 0 00-1-1zM12 13.5V12a1 1 0 00-1-1h-1a1 1 0 00-1 1v1.5M9 11H8a1 1 0 00-1 1v1.5h3V12a1 1 0 00-1-1zM6 8.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v1.5h3z" />
      </svg>
    );
  if (lowerCaseName.includes('sem lactose'))
    return (
      <svg {...svgProps}>
        <path d="M8 8v9h8V8H8zm2 2h4M8 5l4-3 4 3v2H8V5z" />
        <path d="M3 3l18 18" />
      </svg>
    );
  if (lowerCaseName.includes('contém lactose'))
    return (
      <svg {...svgProps}>
        <path d="M8 8v9h8V8H8zm2 2h4M8 5l4-3 4 3v2H8V5z" />
      </svg>
    );
  if (lowerCaseName.includes('sem glúten'))
    return (
      <svg {...svgProps}>
        <path d="M12 2L8 6v4l4 4 4-4V6l-4-4zM8 12l-4 4v4h4v-4l4-4-4-4zM16 12l4 4v4h-4v-4l-4-4 4-4z" />
        <path d="M3 3l18 18" />
      </svg>
    );
  if (lowerCaseName.includes('contém glúten'))
    return (
      <svg {...svgProps}>
        <path d="M12 2L8 6v4l4 4 4-4V6l-4-4zM8 12l-4 4v4h4v-4l4-4-4-4zM16 12l4 4v4h-4v-4l-4-4 4-4z" />
      </svg>
    );
  if (lowerCaseName.includes('light'))
    return (
      <svg {...svgProps}>
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 12l6-6" />
        <path d="M12 6V2" />
      </svg>
    );
  if (lowerCaseName.includes('diet'))
    return (
      <svg {...svgProps}>
        <path d="M3 12h3m15 0h-3M12 3v3m0 15v-3" />
        <path d="M12 19a7 7 0 100-14 7 7 0 000 14z" />
        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
      </svg>
    );
  if (lowerCaseName.includes('vegano'))
    return (
      <svg {...svgProps}>
        <path d="M12 2c-5.523 0-10 4.477-10 10s10 10 10 10 10-4.477 10-10S17.523 2 12 2z" />
        <path d="M10 17l-4-4 1.5-1.5L10 14l4.5-4.5L16 11l-6 6z" stroke="white" fill="white" />
      </svg>
    );
  if (lowerCaseName.includes('vegetariano'))
    return (
      <svg {...svgProps}>
        <path d="M12 2c5.523 0 10 4.477 10 10s-10 10-10 10S2 17.523 2 12 6.477 2 12 2z" />
        <path d="M16 8.5a4 4 0 10-8 0c0 4 4 8 4 8s4-4 4-8z" />
      </svg>
    );
  return (
    <svg {...svgProps}>
      <path d="M12 2l-4 4h8l-4-4zM6 8h12v2H6zm0 4h12v2H6zm0 4h12v2H6z" />
    </svg>
  );
};

/* ===== Seletor de Tags ===== */
export const TagSelector = ({ allTags, selectedTagIds, onToggle, onCreateTag }) => (
  <Box>
    <Typography variant="subtitle1" gutterBottom mt={2}>
      Adicionar tags (Opcional)
    </Typography>
    <Grid container spacing={2}>
      {allTags.map((tag) => {
        const label = tag.traducoes?.[0]?.nome || `Tag ${tag.id}`;
        const active = selectedTagIds.map(String).includes(String(tag.id));
        return (
          <Grid item key={tag.id} xs={4} sm={3} md={2}>
            <Button
              variant={active ? 'contained' : 'outlined'}
              onClick={() => onToggle(tag.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 90,
                width: '100%',
                textTransform: 'none',
                border: '1px solid',
                borderColor: active ? 'primary.main' : '#ddd',
              }}
            >
              {getTagIcon(label)}
              <Typography variant="caption" sx={{ mt: 1 }}>
                {label}
              </Typography>
            </Button>
          </Grid>
        );
      })}
      <Grid item xs={4} sm={3} md={2}>
        <Button
          variant="outlined"
          onClick={onCreateTag}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 90,
            width: '100%',
            textTransform: 'none',
            borderColor: '#ddd',
          }}
        >
          <AddIcon />
          <Typography variant="caption" sx={{ mt: 1 }}>
            Criar nova Tag
          </Typography>
        </Button>
      </Grid>
    </Grid>
  </Box>
);

/* ===== Modal nova Tag ===== */
export const CreateTagModal = ({ open, onClose, onSave, languages }) => {
  const [data, setData] = useState({ traducoes: [] });
  const [lang, setLang] = useState('POR');
  const setNome = (v) => {
    const i = data.traducoes.findIndex((t) => t.idioma === lang);
    const tr =
      i > -1
        ? data.traducoes.map((t, k) => (k === i ? { ...t, nome: v } : t))
        : [...data.traducoes, { idioma: lang, nome: v }];
    setData({ traducoes: tr });
  };
  const getNome = () => data.traducoes.find((t) => t.idioma === lang)?.nome || '';
  const save = async () => {
    await onSave(data);
    setData({ traducoes: [] });
    onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Criar nova Tag</DialogTitle>
      <DialogContent>
        <Tabs
          value={lang}
          onChange={(e, v) => setLang(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {languages.map((l) => (
            <Tab key={l} label={l} value={l} />
          ))}
        </Tabs>
        <TextField
          autoFocus
          margin="dense"
          value={getNome()}
          onChange={(e) => setNome(e.target.value)}
          fullWidth
          variant="outlined"
          placeholder="Ex.: Vegano, Sem glúten…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={save} variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ===== Complementos (usa CurrencyBRL) ===== */
export const ComplementosManager = ({ complementos, onChange }) => {
  const setGrupo = (idx, patch) => {
    const next = [...(complementos || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const addGrupo = () =>
    onChange([
      ...(complementos || []),
      {
        titulo: '',
        qtd_minima: 1,
        qtd_maxima: 1,
        ativo: true,
        opcoes: [{ nome: '', preco: 0, codigo_pdv: '', ativo: true }],
      },
    ]);
  const delGrupo = (idx) => onChange((complementos || []).filter((_, i) => i !== idx));
  const addOpcao = (g) =>
    setGrupo(g, {
      opcoes: [
        ...(complementos[g]?.opcoes || []),
        { nome: '', preco: 0, codigo_pdv: '', ativo: true },
      ],
    });
  const setOpcao = (g, o, patch) => {
    const next = [...(complementos || [])];
    const ops = [...(next[g].opcoes || [])];
    ops[o] = { ...ops[o], ...patch };
    next[g] = { ...next[g], opcoes: ops };
    onChange(next);
  };
  const delOpcao = (g, o) => {
    const next = [...(complementos || [])];
    next[g] = {
      ...next[g],
      opcoes: (next[g].opcoes || []).filter((_, i) => i !== o),
    };
    onChange(next);
  };

  return (
    <Box>
      {(complementos || []).map((g, gi) => (
        <Paper key={gi} variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'error.main' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">Grupo de Complemento {gi + 1}</Typography>
            <Button color="error" onClick={() => delGrupo(gi)}>
              Excluir Grupo
            </Button>
          </Stack>
          <TextField
            label="Título do complemento"
            fullWidth
            margin="dense"
            value={g.titulo || ''}
            onChange={(e) => setGrupo(gi, { titulo: e.target.value })}
          />
          <Stack direction="row" spacing={2} mt={1}>
            {['qtd_minima', 'qtd_maxima'].map((k) => (
              <TextField
                key={k}
                label={k === 'qtd_minima' ? 'Qtd mínima' : 'Qtd máxima'}
                type="number"
                fullWidth
                margin="dense"
                value={g[k] ?? (k === 'qtd_minima' ? 1 : 1)}
                onChange={(e) => setGrupo(gi, { [k]: Number(e.target.value) })}
                sx={{
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            ))}
          </Stack>
          <FormControlLabel
            control={<Switch checked={!!g.ativo} onChange={(e) => setGrupo(gi, { ativo: e.target.checked })} />}
            label="Ativo"
          />
          <Divider sx={{ my: 2 }} />
          {(g.opcoes || []).map((o, oi) => (
            <Box key={oi} sx={{ ml: 2, mb: 2, borderLeft: '2px solid #ddd', pl: 2 }}>
              <Typography variant="subtitle2">Complemento {oi + 1}</Typography>
              <TextField
                label="Nome da opção"
                fullWidth
                margin="dense"
                value={o.nome || ''}
                onChange={(e) => setOpcao(gi, oi, { nome: e.target.value })}
              />
              <CurrencyBRL
                label="Preço (Opcional)"
                value={o.preco ?? 0}
                onChange={(num) => setOpcao(gi, oi, { preco: num })}
              />
              <TextField
                label="Código PDV (Opcional)"
                fullWidth
                margin="dense"
                value={o.codigo_pdv || ''}
                onChange={(e) => setOpcao(gi, oi, { codigo_pdv: e.target.value })}
              />
              <FormControlLabel
                control={<Switch checked={!!o.ativo} onChange={(e) => setOpcao(gi, oi, { ativo: e.target.checked })} />}
                label="Ativo"
              />
              <Button size="small" color="error" onClick={() => delOpcao(gi, oi)}>
                Excluir
              </Button>
            </Box>
          ))}
          <Button size="small" onClick={() => addOpcao(gi)}>
            + Adicionar nova opção
          </Button>
        </Paper>
      ))}
      <Button variant="outlined" sx={{ mt: 2 }} onClick={addGrupo}>
        Adicionar grupo de complementos
      </Button>
    </Box>
  );
};

/* ===== Campos de tradução ===== */
export const TranslationFields = ({
  type,
  activeLang,
  setActiveLang,
  languages,
  getTrad,
  setTrad,
}) => (
  <>
    <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 3 }}>
      <Tabs value={activeLang} onChange={(e, v) => setActiveLang(v)}>
        {languages.map((l) => (
          <Tab key={l} label={l} value={l} />
        ))}
      </Tabs>
    </Box>
    <Typography variant="subtitle1" gutterBottom>
      Nome do {type}
    </Typography>
    <TextField
      value={getTrad('nome')}
      onChange={(e) => setTrad('nome', e.target.value)}
      fullWidth
      margin="normal"
    />
    <Typography variant="subtitle1" gutterBottom mt={2}>
      Descrição (Opcional)
    </Typography>
    <TextField
      value={getTrad('descricao')}
      onChange={(e) => setTrad('descricao', e.target.value)}
      fullWidth
      multiline
      rows={4}
      margin="normal"
    />
  </>
);
